import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  TrainingPlannerContainer,
  PageSubtitle,
  FormAreaWrapper,
  FormSection,
  FormTitle,
  FormGrid,
  FormGroup,
  Label,
  Select,
  Input,
  SubmitButton,
  AddButton,
  HeaderContainer,
  ButtonWrapper,
  SessionsList,
  SessionItem,
  SessionDate,
  SessionType,
  SessionLocation,
  SessionAthletes,
  SessionTime,
  StartButton,
  SectionHeader,
  EmptyState,
  ActionButtonsContainer,
  EditButton,
  DeleteButton,
} from './styles';
import styled from 'styled-components';
import { PageTitle } from '../CoachDashboard/styles';
import { athleteAPI, equipmentAPI, trainingSessionAPI, bananaCupAPI } from '../../services/api';
import { hasPlanOrHigher } from '../../utils/subscriptionUtils';
import { Athlete as AthleteType } from '../../types/mongodb';

// MongoDB types
interface Athlete {
  _id: string;
  firstName: string;
  lastName: string;
}

interface Equipment {
  _id: string;
  name: string;
  type: 'suit' | 'skis' | 'shoes';
  athleteId: string;
}

interface BananaCupCompetition {
  _id: string;
  competitionNumber: number;
  date?: string;
  startTime?: string;
  endTime?: string;
  location?: string;
  weather?: string;
  hillSize?: string;
  isCompleted?: boolean;
}

interface BananaCup {
  _id: string;
  name: string;
  athleteIds: (string | { _id: string })[];
  competitions: BananaCupCompetition[];
}

interface TrainingSession {
  _id: string;
  trainingType: 'ski_jumping' | 'competition' | 'ground_training' | 'cross_country' | 'banana_cup';
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  weather?: string;
  category?: string;
  athleteIds: (string | { _id: string; firstName: string; lastName: string })[];
  notes?: string;
  jumps?: any[]; // For SKI JUMPING sessions
  competitionJumps?: any[]; // For COMPETITION sessions
  groundTrainings?: any[]; // For GROUND TRAINING sessions
  crossCountryTrainings?: any[]; // For CROSS COUNTRY sessions
  bananaCupId?: string;
  bananaCupCompetitionId?: string;
  bananaCupCompetitionNumber?: number;
  bananaCupName?: string;
  createdAt: string;
  updatedAt: string;
}

// Training plan form data type
interface TrainingPlanFormData {
  trainingType: 'ski_jumping' | 'competition' | 'ground_training' | 'cross_country' | 'banana_cup';
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  category: string;
  athleteIds: string[];
  bananaCupId?: string;
  bananaCupCompetitionId?: string;
  bananaCupCompetitionNumber?: number;
  bananaCupName?: string;
}

interface TrainingPlannerProps {
  readOnly?: boolean; // When true, hides add/edit/delete buttons (for athlete view)
}

const TrainingPlanner: React.FC<TrainingPlannerProps> = ({ readOnly = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [formData, setFormData] = useState<TrainingPlanFormData>({
    trainingType: 'ski_jumping',
    date: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    location: '',
    category: '',
    athleteIds: [],
  });

  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [bananaCups, setBananaCups] = useState<BananaCup[]>([]);
  const [bananaCupSelection, setBananaCupSelection] = useState<string>('');
  const [trainingSessions, setTrainingSessions] = useState<TrainingSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedAthleteId, setSelectedAthleteId] = useState<string>('all');
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const bananaCupCompetitionOptions = useMemo(() => {
    return bananaCups.flatMap(cup =>
      (cup.competitions || [])
        .filter(competition => !competition.isCompleted)
        .map(competition => {
          const dateLabel = competition.date
            ? ` • ${new Date(competition.date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}`
            : '';
          return {
            key: `${cup._id}::${competition._id}`,
            label: `${cup.name} • Competition ${competition.competitionNumber}${dateLabel}`,
            cup,
            competition,
          };
        })
    );
  }, [bananaCups]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch data function
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [athleteData, sessionsResponse, bananaCupData] = await Promise.all([
        athleteAPI.getAll(),
        trainingSessionAPI.getAll(undefined, undefined, true), // Load all sessions for training planner
        bananaCupAPI.getAll().catch(() => []),
      ]);
      // Handle response - when all=true, returns array directly, otherwise paginated object
      const sessionsData = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse.sessions || []);
      // Show all athletes; lock selection by plan in UI
      setAthletes(athleteData);
      setTrainingSessions(sessionsData);
      setBananaCups(Array.isArray(bananaCupData) ? bananaCupData : []);
      setError(null);
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Fetch athletes and training sessions from MongoDB
    fetchData();
  }, [fetchData]);

  // Refresh data when component comes into focus (user navigates back to this page)
  useEffect(() => {
    const handleFocus = () => {
      fetchData();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchData]);

  // Open "Plan new training session" form when navigated from coach home ADD menu
  useEffect(() => {
    const state = location.state as { openPlanNewSession?: boolean } | null;
    if (state?.openPlanNewSession) {
      setShowForm(true);
      setError(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.state]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'trainingType') {
      const nextType = value as TrainingPlanFormData['trainingType'];
      setFormData(prev => ({
        ...prev,
        trainingType: nextType,
        category: nextType === 'competition' ? prev.category : '',
        bananaCupId: nextType === 'banana_cup' ? prev.bananaCupId : undefined,
        bananaCupCompetitionId: nextType === 'banana_cup' ? prev.bananaCupCompetitionId : undefined,
        bananaCupCompetitionNumber: nextType === 'banana_cup' ? prev.bananaCupCompetitionNumber : undefined,
        bananaCupName: nextType === 'banana_cup' ? prev.bananaCupName : undefined,
        athleteIds: nextType === 'banana_cup' ? [] : prev.athleteIds,
      }));
      if (nextType !== 'banana_cup') {
        setBananaCupSelection('');
      }
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const normalizeCupAthleteIds = (cup: BananaCup) =>
    cup.athleteIds.map(id => (typeof id === 'string' ? id : id._id));

  const handleAthleteSelection = (athleteId: string) => {
    setFormData(prev => ({
      ...prev,
      athleteIds: prev.athleteIds.includes(athleteId)
        ? prev.athleteIds.filter(id => id !== athleteId)
        : [...prev.athleteIds, athleteId]
    }));
  };

  const handleBananaCupSelectionChange = (selectionKey: string) => {
    setBananaCupSelection(selectionKey);
    if (!selectionKey) {
      setFormData(prev => ({
        ...prev,
        bananaCupId: undefined,
        bananaCupCompetitionId: undefined,
        bananaCupCompetitionNumber: undefined,
        bananaCupName: undefined,
        athleteIds: [],
      }));
      return;
    }
    const option = bananaCupCompetitionOptions.find(opt => opt.key === selectionKey);
    if (!option) {
      return;
    }
    const normalizedAthletes = normalizeCupAthleteIds(option.cup);
    setFormData(prev => ({
      ...prev,
      bananaCupId: option.cup._id,
      bananaCupCompetitionId: option.competition._id,
      bananaCupCompetitionNumber: option.competition.competitionNumber,
      bananaCupName: option.cup.name,
      date: option.competition.date || prev.date,
      startTime: option.competition.startTime || prev.startTime,
      endTime: option.competition.endTime || prev.endTime,
      location: option.competition.location || prev.location,
      athleteIds: normalizedAthletes,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate that end time is after start time
    if (formData.startTime && formData.endTime) {
      const startTime = new Date(`2000-01-01T${formData.startTime}`);
      const endTime = new Date(`2000-01-01T${formData.endTime}`);
      
      if (endTime <= startTime) {
        setError('End time must be after start time');
        return;
      }
    }
    
    // Validate that at least one athlete is selected
    if (formData.athleteIds.length === 0) {
      setError('Please select at least one athlete');
      return;
    }

    if (formData.trainingType === 'banana_cup' && !formData.bananaCupCompetitionId) {
      setError('Please select a Banana Cup competition for this plan');
      return;
    }
    
    try {
      // Prepare session data for API
      const sessionData: any = {
        trainingType: formData.trainingType,
        date: formData.date,
        startTime: formData.startTime,
        endTime: formData.endTime,
        location: formData.location,
        athleteIds: formData.athleteIds,
      };

      // Add optional fields
      if (formData.category && formData.trainingType === 'competition') {
        sessionData.category = formData.category;
      }

      if (formData.trainingType === 'banana_cup') {
        sessionData.bananaCupId = formData.bananaCupId;
        sessionData.bananaCupCompetitionId = formData.bananaCupCompetitionId;
        sessionData.bananaCupCompetitionNumber = formData.bananaCupCompetitionNumber;
        sessionData.bananaCupName = formData.bananaCupName;
      }

      if (editingSessionId) {
        // Update existing session
        console.log('Updating training plan with data:', sessionData);
        const updatedSession = await trainingSessionAPI.update(editingSessionId, sessionData);
        console.log('Training plan updated successfully:', updatedSession);
        
        // Update the session in local state
        setTrainingSessions(prev => prev.map(s => s._id === updatedSession._id ? updatedSession : s));
      } else {
        // Create new session
        sessionData.jumps = [];
        sessionData.competitionJumps = [];
        sessionData.groundTrainings = [];
        
        console.log('Creating training plan with data:', sessionData);
        const newSession = await trainingSessionAPI.create(sessionData);
        console.log('Training plan created successfully:', newSession);
        
        // Add the new session to the local state
        setTrainingSessions(prev => [newSession, ...prev]);
      }
      
      // Reset form
      setFormData({
        trainingType: 'ski_jumping',
        date: new Date().toISOString().split('T')[0],
        startTime: '',
        endTime: '',
        location: '',
        category: '',
        athleteIds: [],
        bananaCupId: undefined,
        bananaCupCompetitionId: undefined,
        bananaCupCompetitionNumber: undefined,
        bananaCupName: undefined,
      });
      setBananaCupSelection('');
      setShowForm(false);
      setEditingSessionId(null);
      setError(null);
    } catch (err) {
      console.error('Error saving training plan:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to ${editingSessionId ? 'update' : 'create'} training plan: ${errorMessage}`);
    }
  };

  const handleAddButtonClick = () => {
    setEditingSessionId(null);
    setFormData({
      trainingType: 'ski_jumping',
      date: new Date().toISOString().split('T')[0],
      startTime: '',
      endTime: '',
      location: '',
      category: '',
      athleteIds: [],
      bananaCupId: undefined,
      bananaCupCompetitionId: undefined,
      bananaCupCompetitionNumber: undefined,
      bananaCupName: undefined,
    });
    setShowForm(true);
    setError(null);
    setBananaCupSelection('');
  };

  const handleEditSession = (session: TrainingSession) => {
    // Extract athlete IDs (handle both string array and populated objects)
    const athleteIds = session.athleteIds.map((aid: any) => 
      typeof aid === 'object' && aid !== null ? aid._id : aid
    );
    
    setFormData({
      trainingType: session.trainingType as TrainingPlanFormData['trainingType'],
      date: session.date,
      startTime: session.startTime || '',
      endTime: session.endTime || '',
      location: session.location || '',
      category: session.category || '',
      athleteIds: athleteIds,
      bananaCupId: session.bananaCupId,
      bananaCupCompetitionId: session.bananaCupCompetitionId,
      bananaCupCompetitionNumber: session.bananaCupCompetitionNumber,
      bananaCupName: session.bananaCupName,
    });
    if (session.trainingType === 'banana_cup' && session.bananaCupId && session.bananaCupCompetitionId) {
      setBananaCupSelection(`${session.bananaCupId}::${session.bananaCupCompetitionId}`);
    } else {
      setBananaCupSelection('');
    }
    setEditingSessionId(session._id);
    setShowForm(true);
    setError(null);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (!window.confirm('Are you sure you want to delete this planned session?')) {
      return;
    }
    
    try {
      await trainingSessionAPI.delete(sessionId);
      // Remove from local state
      setTrainingSessions(prev => prev.filter(s => s._id !== sessionId));
      setError(null);
    } catch (err) {
      console.error('Error deleting training plan:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to delete training plan: ${errorMessage}`);
    }
  };

  const handleStartSession = (session: TrainingSession) => {
    if (session.trainingType === 'banana_cup') {
      if (!session.bananaCupId || !session.bananaCupCompetitionId) {
        setError('Selected Banana Cup plan is missing competition details. Please edit the plan.');
        return;
      }
      navigate('/coach/banana-cup', {
        state: {
          bananaCupId: session.bananaCupId,
          bananaCupCompetitionId: session.bananaCupCompetitionId,
          bananaCupCompetitionNumber: session.bananaCupCompetitionNumber,
        },
      });
      return;
    }

    // Navigate to Training Sessions page with pre-filled data and session ID
    navigate('/coach/training-sessions', {
      state: {
        prefillSession: {
          trainingType: session.trainingType,
          date: session.date,
          startTime: session.startTime,
          endTime: session.endTime,
          location: session.location,
          weather: session.weather || '',
          athleteIds: session.athleteIds,
          category: session.category || '',
          sessionId: session._id, // Pass the session ID to update instead of creating new
        }
      }
    });
  };

  const getAthleteNames = (
    athleteIds: (string | { _id: string; firstName: string; lastName: string })[]
  ) => {
    if (!athleteIds || athleteIds.length === 0) {
      return '';
    }
    
    return athleteIds
      .map(idOrAthlete => {
        // Check if it's a populated athlete object
        if (typeof idOrAthlete === 'object' && idOrAthlete !== null && 'firstName' in idOrAthlete) {
          return `${idOrAthlete.firstName} ${idOrAthlete.lastName}`;
        }
        // Otherwise it's an ID string, look it up
        const athlete = athletes.find(a => a._id === idOrAthlete);
        return athlete ? `${athlete.firstName} ${athlete.lastName}` : 'Unknown';
      })
      .filter(name => name !== 'Unknown')
      .join(', ');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return '';
    return timeString;
  };

  const formatEnumLabel = (value?: string) => (value || '').replace(/_/g, ' ');

  // Calendar functions
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    // Get day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    // Convert to Monday-based week (0 = Monday, 1 = Tuesday, ..., 6 = Sunday)
    return day === 0 ? 6 : day - 1;
  };

  // Get all sessions for calendar (including past sessions and competitions)
  const getAllSessionsForCalendar = () => {
    let filteredSessions = trainingSessions;
    
    // Filter by selected athlete if one is chosen
    if (selectedAthleteId !== 'all') {
      filteredSessions = trainingSessions.filter(session => {
        // Check if the selected athlete is in the session's athleteIds
        return session.athleteIds.some(athleteId => {
          // Handle both string IDs and populated athlete objects
          if (typeof athleteId === 'string') {
            return athleteId === selectedAthleteId;
          }
          if (typeof athleteId === 'object' && athleteId !== null && '_id' in athleteId) {
            return (athleteId as { _id: string })._id === selectedAthleteId;
          }
          return false;
        });
      });
    }
    
    return filteredSessions
      .sort((a, b) => {
        // Sort by date first, then by start time
        const dateCompare = new Date(a.date).getTime() - new Date(b.date).getTime();
        if (dateCompare !== 0) return dateCompare;
        return (a.startTime || '').localeCompare(b.startTime || '');
      });
  };

  // Get color for training type (matching athlete detail page)
  const getTrainingTypeColor = (trainingType: string) => {
    switch (trainingType) {
      case 'ski_jumping':
        return { bg: 'rgba(33, 150, 243, 0.15)', color: '#1976D2' };
      case 'competition':
        return { bg: 'rgba(255, 87, 34, 0.1)', color: '#FF5722' };
      case 'ground_training':
        return { bg: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50' };
      case 'cross_country':
        return { bg: 'rgba(33, 150, 243, 0.1)', color: '#2196F3' };
      default:
        return { bg: 'rgba(0, 0, 0, 0.05)', color: 'var(--text-gray)' };
    }
  };

  const getSessionsForDate = (date: Date) => {
    // Format date as YYYY-MM-DD in local timezone (not UTC)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    return getAllSessionsForCalendar().filter(session => {
      // Session date might already be a string in YYYY-MM-DD format
      if (typeof session.date === 'string' && session.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return session.date === dateString;
      }
      
      // Otherwise, parse it as a date and format it
      const sessionDate = new Date(session.date);
      const sessionYear = sessionDate.getFullYear();
      const sessionMonth = String(sessionDate.getMonth() + 1).padStart(2, '0');
      const sessionDay = String(sessionDate.getDate()).padStart(2, '0');
      const sessionDateString = `${sessionYear}-${sessionMonth}-${sessionDay}`;
      
      return sessionDateString === dateString;
    });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Get all future sessions (planned sessions)
  // Exclude sessions that have been started (have jumps, competitionJumps, groundTrainings, or crossCountryTrainings)
  const getFutureSessions = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return trainingSessions
      .filter(session => {
        const sessionDate = new Date(session.date);
        sessionDate.setHours(0, 0, 0, 0);
        
        // Only show future sessions (date >= today)
        if (sessionDate < now) {
          return false;
        }
        
        // Exclude sessions that have been started (have actual data)
        const hasJumps = session.jumps && Array.isArray(session.jumps) && session.jumps.length > 0;
        const hasCompetitionJumps = session.competitionJumps && Array.isArray(session.competitionJumps) && session.competitionJumps.length > 0;
        const hasGroundTrainings = session.groundTrainings && Array.isArray(session.groundTrainings) && session.groundTrainings.length > 0;
        const hasCrossCountryTrainings = session.crossCountryTrainings && Array.isArray(session.crossCountryTrainings) && session.crossCountryTrainings.length > 0;
        
        // If session has any data, it's been started, so don't show it as pre-planned
        if (hasJumps || hasCompetitionJumps || hasGroundTrainings || hasCrossCountryTrainings) {
          return false;
        }
        
        // Only show sessions that are future and haven't been started
        return true;
      })
      .sort((a, b) => {
        // Sort by date first, then by start time
        const dateCompare = new Date(a.date).getTime() - new Date(b.date).getTime();
        if (dateCompare !== 0) return dateCompare;
        return (a.startTime || '').localeCompare(b.startTime || '');
      });
  };

  if (isLoading) {
    return (
      <TrainingPlannerContainer>
        <PageTitle>Training Planner</PageTitle>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#fff' }}>
          Loading training planner...
        </div>
      </TrainingPlannerContainer>
    );
  }

  return (
    <TrainingPlannerContainer>
      <HeaderContainer>
        <div>
          <PageTitle>Training Planner</PageTitle>
          <PageSubtitle>
            Plan and schedule your future training sessions to optimize athlete performance.
          </PageSubtitle>
        </div>
      </HeaderContainer>
      <ButtonWrapper style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        {!readOnly && (
          <AddButton onClick={handleAddButtonClick} style={{ height: '44px', display: 'flex', alignItems: 'center', marginTop: 0 }}>
            PLAN NEW TRAINING SESSION
          </AddButton>
        )}
        <CalendarIconButton onClick={() => setShowCalendar(!showCalendar)}>
          <CalendarIcon />
        </CalendarIconButton>
      </ButtonWrapper>

      {error && (
        <div style={{ 
          background: 'rgba(255, 0, 0, 0.1)', 
          border: '1px solid #ff0000', 
          borderRadius: '8px', 
          padding: '1rem', 
          marginBottom: '1rem',
          color: '#fff'
        }}>
          {error}
        </div>
      )}

      {showForm && (
        <FormAreaWrapper>
          <FormSection>
            <FormTitle>{editingSessionId ? 'Edit Training Plan' : 'Plan new training session'}</FormTitle>
            <form onSubmit={handleSubmit}>
            <FormGrid>
              <FormGroup>
                <Label>Type</Label>
                <Select
                  name="trainingType"
                  value={formData.trainingType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="ski_jumping">SKI JUMPING TRAINING</option>
                  <option value="ground_training">GROUND TRAINING</option>
                  <option value="banana_cup">BANANA CUP</option>
                  <option value="competition">COMPETITION</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Date</Label>
                <Input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Start Time</Label>
                <Input
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>End Time</Label>
                <Input
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <Label>Location</Label>
                <Input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Enter training location"
                  required
                />
              </FormGroup>

              {formData.trainingType === 'competition' && (
                <FormGroup>
                  <Label>Category</Label>
                  <Select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select category</option>
                    <option value="olympic_games">OLYMPIC GAMES</option>
                    <option value="world_championships">WORLD CHAMPIONSHIPS</option>
                    <option value="world_cup">WORLD CUP</option>
                    <option value="summer_grand_prix">SUMMER GRAND PRIX</option>
                    <option value="continental_cup">CONTINENTAL CUP</option>
                    <option value="fis_cup">FIS CUP</option>
                    <option value="alpen_cup">ALPEN CUP</option>
                    <option value="national_championships">NATIONAL CHAMPIONSHIPS</option>
                    <option value="national_cup">NATIONAL CUP</option>
                    <option value="junior_world_championships">JUNIOR WORLD CHAMPIONSHIPS</option>
                    <option value="youth_olympic_games">YOUTH OLYMPIC GAMES</option>
                    <option value="eyof">EYOF</option>
                    <option value="opa_games">OPA GAMES</option>
                  </Select>
                </FormGroup>
              )}

            {formData.trainingType === 'banana_cup' && (
              <FormGroup>
                <Label>Banana Cup Competition</Label>
                <Select
                  name="bananaCupSelection"
                  value={bananaCupSelection}
                  onChange={(e) => handleBananaCupSelectionChange(e.target.value)}
                  required
                  disabled={bananaCupCompetitionOptions.length === 0}
                >
                  <option value="">
                    {bananaCupCompetitionOptions.length === 0
                      ? 'Create a Banana Cup first'
                      : 'Select Banana Cup competition'}
                  </option>
                  {bananaCupCompetitionOptions.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                {bananaCupCompetitionOptions.length === 0 && (
                  <p style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.35rem' }}>
                    Create a Banana Cup to enable planning.
                  </p>
                )}
              </FormGroup>
            )}
            </FormGrid>

            <FormGrid>
              <FormGroup style={{ gridColumn: 'span 2' }}>
                <Label>
                  Participating Athletes
                  {formData.trainingType === 'banana_cup' && (
                    <span style={{ fontSize: '0.8rem', color: '#888', marginLeft: '0.5rem' }}>
                      Synced from Banana Cup selection
                    </span>
                  )}
                </Label>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  maxHeight: '150px',
                  overflowY: 'auto',
                  borderRadius: '0.5rem',
                  padding: '0.5rem',
                  background: '#fff',
                  border: '1px solid #e0e6ed'
                }}>
                  {athletes.map(athlete => {
                    const plan = (athlete as any).subscription?.plan || 'free';
                    const canSelect = hasPlanOrHigher(plan, 'hs90');
                    const isDisabled = formData.trainingType === 'banana_cup' || !canSelect;
                    return (
                      <label key={athlete._id} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        padding: '0.25rem',
                        borderRadius: '0.25rem',
                        background: formData.athleteIds.includes(athlete._id) ? 'var(--accent-orange)' : 'transparent',
                        color: formData.athleteIds.includes(athlete._id) ? '#fff' : 'inherit',
                        opacity: canSelect ? 1 : 0.6
                      }}>
                        <input
                          type="checkbox"
                          checked={formData.athleteIds.includes(athlete._id)}
                          onChange={() => {
                            if (formData.trainingType === 'banana_cup' || !canSelect) return;
                            handleAthleteSelection(athlete._id);
                          }}
                          disabled={isDisabled}
                          style={{ 
                            margin: 0,
                            cursor: isDisabled ? 'not-allowed' : 'pointer'
                          }}
                        />
                        {athlete.firstName} {athlete.lastName}{!canSelect ? ' (Upgrade plan)' : ''}
                      </label>
                    );
                  })}
                </div>
              </FormGroup>
            </FormGrid>



            <SubmitButton type="submit" style={{ marginTop: '1rem' }}>
              {editingSessionId ? 'UPDATE TRAINING SESSION' : 'PLAN TRAINING SESSION'}
            </SubmitButton>
            <SubmitButton 
              type="button" 
              onClick={() => {
                setShowForm(false);
                setEditingSessionId(null);
                setFormData({
                  trainingType: 'ski_jumping',
                  date: new Date().toISOString().split('T')[0],
                  startTime: '',
                  endTime: '',
                  location: '',
                  category: '',
                  athleteIds: [],
                  bananaCupId: undefined,
                  bananaCupCompetitionId: undefined,
                  bananaCupCompetitionNumber: undefined,
                  bananaCupName: undefined,
                });
                setBananaCupSelection('');
              }}
              style={{
                background: 'transparent',
                color: 'var(--text-gray)',
                border: '1px solid var(--text-gray)',
                boxShadow: 'none',
                marginTop: '0.5rem',
                width: '100%'
              }}
            >
              Cancel
            </SubmitButton>
          </form>
          </FormSection>
        </FormAreaWrapper>
      )}

      {showCalendar && (
        <CalendarContainer>
          <CalendarHeader>
            <CalendarNavButton onClick={() => navigateMonth('prev')}>
              ‹
            </CalendarNavButton>
            <CalendarTitle>
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </CalendarTitle>
            <CalendarNavButton onClick={() => navigateMonth('next')}>
              ›
            </CalendarNavButton>
          </CalendarHeader>
          <CalendarFilterSection>
            <CalendarFilterLabel>Filter by Athlete:</CalendarFilterLabel>
            <CalendarFilterSelect
              value={selectedAthleteId}
              onChange={(e) => setSelectedAthleteId(e.target.value)}
            >
              <option value="all">All Athletes</option>
              {athletes.map(athlete => (
                <option key={athlete._id} value={athlete._id}>
                  {athlete.firstName} {athlete.lastName}
                </option>
              ))}
            </CalendarFilterSelect>
          </CalendarFilterSection>
          <CalendarGrid>
            {dayNames.map(day => (
              <CalendarDayHeader key={day}>{day}</CalendarDayHeader>
            ))}
            {Array.from({ length: getFirstDayOfMonth(currentMonth) }).map((_, index) => (
              <CalendarDay key={`empty-${index}`} isEmpty />
            ))}
            {Array.from({ length: getDaysInMonth(currentMonth) }).map((_, index) => {
              const day = index + 1;
              const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
              const sessionsForDay = getSessionsForDate(date);
              const isToday = date.toDateString() === new Date().toDateString();
              const isPast = date < new Date() && !isToday;
              
              return (
                <CalendarDay 
                  key={day} 
                  hasSessions={sessionsForDay.length > 0}
                  isToday={isToday}
                  isPast={isPast}
                >
                  <CalendarDayNumber isToday={isToday} isPast={isPast}>{day}</CalendarDayNumber>
                  {sessionsForDay.length > 0 && (
                    <CalendarDaySessions>
                      {sessionsForDay.map(session => {
                        const sessionDate = new Date(session.date);
                        const isSessionPast = sessionDate < new Date();
                        const typeColors = getTrainingTypeColor(session.trainingType);
                        return (
                          <CalendarDaySessionItem 
                            key={session._id} 
                            isPast={isSessionPast}
                            $bgColor={typeColors.bg}
                            $textColor={typeColors.color}
                          >
                            <CalendarDaySessionType isPast={isSessionPast} $textColor={typeColors.color}>
                              {formatEnumLabel(session.trainingType).toUpperCase()}
                            </CalendarDaySessionType>
                            <CalendarDaySessionLocation isPast={isSessionPast}>{session.location}</CalendarDaySessionLocation>
                            {session.startTime && (
                              <CalendarDaySessionTime isPast={isSessionPast}>{formatTime(session.startTime)}</CalendarDaySessionTime>
                            )}
                          </CalendarDaySessionItem>
                        );
                      })}
                    </CalendarDaySessions>
                  )}
                </CalendarDay>
              );
            })}
          </CalendarGrid>
        </CalendarContainer>
      )}

      <SessionsList>
        <SectionHeader>Planned Training Sessions</SectionHeader>
        
        {getFutureSessions().length > 0 ? (
          getFutureSessions().map(session => (
            <SessionItem key={session._id} isPast={false}>
              <div style={{ flex: 1, paddingRight: isMobile ? '0' : '4rem', width: isMobile ? '100%' : 'auto' }}>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'flex-start' : 'center', 
                  gap: isMobile ? '0.5rem' : '1rem', 
                  marginBottom: '0.5rem' 
                }}>
                  <SessionDate isPast={false}>{formatDate(session.date)}</SessionDate>
                  {!isMobile && (
                    <SessionType isPast={false}>
                      {session.trainingType === 'banana_cup' ? (
                        <>
                          BANANA CUP
                          {session.bananaCupName && ` • ${session.bananaCupName}`}
                          {session.bananaCupCompetitionNumber && ` • COMP ${session.bananaCupCompetitionNumber}`}
                        </>
                      ) : (
                        <>
                          {formatEnumLabel(session.trainingType).toUpperCase()}
                          {session.category && ` • ${formatEnumLabel(session.category).toUpperCase()}`}
                        </>
                      )}
                    </SessionType>
                  )}
                  {!readOnly && !isMobile && (
                    <ActionButtonsContainer style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginLeft: 'auto'
                    }}>
                      <EditButton 
                        onClick={() => handleEditSession(session)}
                        title="Edit session"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </EditButton>
                      <DeleteButton 
                        onClick={() => handleDeleteSession(session._id)}
                        title="Delete session"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </DeleteButton>
                      <StartButton onClick={() => handleStartSession(session)}>
                        {session.trainingType === 'banana_cup' ? 'OPEN BANANA CUP' : 'START SESSION'}
                      </StartButton>
                    </ActionButtonsContainer>
                  )}
                </div>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '0.25rem' : '0.5rem',
                  marginBottom: isMobile ? '0.5rem' : '0.25rem'
                }}>
                  <SessionLocation isPast={false}>{session.location}</SessionLocation>
                  {session.startTime && session.endTime && (
                    <SessionTime isPast={false}>
                      {formatTime(session.startTime)} - {formatTime(session.endTime)}
                    </SessionTime>
                  )}
                </div>
                {isMobile && (
                  <SessionType isPast={false} style={{ marginBottom: '0.5rem' }}>
                    {session.trainingType === 'banana_cup' ? (
                      <>
                        BANANA CUP
                        {session.bananaCupName && ` • ${session.bananaCupName}`}
                        {session.bananaCupCompetitionNumber && ` • COMP ${session.bananaCupCompetitionNumber}`}
                      </>
                    ) : (
                      <>
                        {formatEnumLabel(session.trainingType).toUpperCase()}
                        {session.category && ` • ${formatEnumLabel(session.category).toUpperCase()}`}
                      </>
                    )}
                  </SessionType>
                )}
                <SessionAthletes isPast={false}>
                  {getAthleteNames(session.athleteIds)}
                </SessionAthletes>
                {!readOnly && isMobile && (
                  <ActionButtonsContainer style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    marginTop: '1rem',
                    paddingTop: '1rem',
                    width: '100%',
                    justifyContent: 'flex-start'
                  }}>
                    <StartButton onClick={() => handleStartSession(session)}>
                      {session.trainingType === 'banana_cup' ? 'OPEN BANANA CUP' : 'START SESSION'}
                    </StartButton>
                    <EditButton 
                      onClick={() => handleEditSession(session)}
                      title="Edit session"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </EditButton>
                    <DeleteButton 
                      onClick={() => handleDeleteSession(session._id)}
                      title="Delete session"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </DeleteButton>
                  </ActionButtonsContainer>
                )}
              </div>
            </SessionItem>
          ))
        ) : (
          <EmptyState>
            <div style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>No planned sessions</div>
            {!readOnly && (
              <div style={{ color: '#888' }}>Click the PLAN NEW TRAINING SESSION button to plan your first training session.</div>
            )}
            {readOnly && (
              <div style={{ color: '#888' }}>No training sessions have been planned yet.</div>
            )}
          </EmptyState>
        )}
      </SessionsList>
    </TrainingPlannerContainer>
  );
};

// Calendar styled components
const CalendarIconButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  border: none;
  background: var(--accent-orange);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
  box-shadow: var(--shadow-sm);
  padding: 0;

  &:hover {
    background: var(--accent-orange-dark);
    box-shadow: var(--shadow-md);
  }

  &:active {
    box-shadow: var(--shadow-xs);
  }
`;

const CalendarIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
    <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const CalendarContainer = styled.div`
  background: var(--bg-sidebar);
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.06);
  margin-bottom: 2rem;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const CalendarNavButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: var(--accent-orange);
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Poppins', sans-serif;

  &:hover {
    background: var(--accent-orange-dark);
  }
`;

const CalendarTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 900;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;
`;

const CalendarFilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 0.5rem;
`;

const CalendarFilterLabel = styled.label`
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
`;

const CalendarFilterSelect = styled.select`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(0,0,0,0.1);
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  background: #fff;
  color: var(--text-gray);
  cursor: pointer;
  transition: border-color 0.2s;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: var(--accent-orange);
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
  margin-bottom: 2rem;
`;

const CalendarDayHeader = styled.div`
  text-align: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  padding: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const CalendarDay = styled.div<{ isEmpty?: boolean; hasSessions?: boolean; isToday?: boolean; isPast?: boolean }>`
  min-height: 120px;
  border-radius: 0.5rem;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  background: ${props => {
    if (props.isEmpty) return 'transparent';
    if (props.isToday) return 'rgba(255, 150, 0, 0.1)';
    if (props.hasSessions && !props.isPast) return 'rgba(255, 150, 0, 0.05)';
    if (props.hasSessions && props.isPast) return 'rgba(0, 0, 0, 0.02)';
    return 'transparent';
  }};
  border: ${props => props.isToday ? '2px solid var(--accent-orange)' : '1px solid rgba(0,0,0,0.05)'};
  cursor: ${props => props.isEmpty ? 'default' : 'pointer'};
  transition: background 0.2s;
  overflow: hidden;
  opacity: ${props => props.isPast ? 0.7 : 1};

  &:hover {
    background: ${props => {
      if (props.isEmpty) return 'transparent';
      return 'rgba(255, 150, 0, 0.1)';
    }};
  }
`;

const CalendarDayNumber = styled.span<{ isToday?: boolean; isPast?: boolean }>`
  font-size: 0.85rem;
  font-weight: ${props => props.isToday ? 900 : 600};
  color: ${props => {
    if (props.isToday) return 'var(--accent-orange)';
    if (props.isPast) return '#bbb';
    return 'var(--text-gray)';
  }};
  font-family: 'Poppins', sans-serif;
  margin-bottom: 0.25rem;
  flex-shrink: 0;
`;

const CalendarDaySessions = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  overflow-y: auto;
  max-height: 100%;
`;

const CalendarDaySessionItem = styled.div<{ isPast?: boolean; $bgColor?: string; $textColor?: string }>`
  width: 100%;
  padding: 0.3rem;
  background: ${props => props.$bgColor || 'transparent'};
  border-radius: 0.25rem;
  border: none;
  opacity: ${props => props.isPast ? 0.6 : 1};
`;

const CalendarDaySessionType = styled.div<{ isPast?: boolean; $textColor?: string }>`
  font-size: 0.55rem;
  font-weight: 700;
  color: ${props => props.$textColor || 'var(--accent-orange)'};
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 0.15rem;
  line-height: 1.2;
`;

const CalendarDaySessionLocation = styled.div<{ isPast?: boolean }>`
  font-size: 0.5rem;
  color: ${props => props.isPast ? '#bbb' : 'var(--text-gray)'};
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  margin-bottom: 0.1rem;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CalendarDaySessionTime = styled.div<{ isPast?: boolean }>`
  font-size: 0.45rem;
  color: #bbb;
  font-family: 'Poppins', sans-serif;
  line-height: 1.2;
`;

export default TrainingPlanner; 